import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { AppIcon, AppText, BaseInput, Button, IconButton, Row, styled, useTheme, } from '@streem/ui-react';
import { useRoomSession } from '../../../context/room_session';
import { useObservable } from '../../../hooks/rxjs';
import { getProtobufTimestamp } from '../../../util/get_protobuf_timestamp';
import { useBookmarkToSelect } from '../../../hooks/use_bookmark_to_select';
export const InCallBookmarks = () => {
    const theme = useTheme();
    const roomSession = useRoomSession();
    const [bookmarks] = useObservable(roomSession.bookmarks.bookmarks);
    const [callStartTime] = useObservable(roomSession.bookmarks.callStartTime);
    const addBookmark = () => {
        roomSession.bookmarks.addBookmark({
            timestamp: getProtobufTimestamp(),
        });
    };
    const updateBookmark = (id, bookmark, label) => {
        roomSession.bookmarks.updateBookmark(id, {
            ...bookmark,
            label,
        });
    };
    const deleteBookmark = async (id) => {
        await roomSession.bookmarks.deleteBookmark(id);
    };
    return (_jsxs(_Fragment, { children: [_jsx(Row, { justifyContent: "center", children: _jsxs(Button, { variant: "tertiary", onClick: addBookmark, children: [_jsx(AppIcon, { color: theme.colors.azure, name: "AddBookmarkIcon" }), ' ', _jsx("span", { style: { marginLeft: '6px', fontSize: '18px' }, children: "Add Bookmark" })] }) }), _jsx(BookmarkListWrapper, { "data-testid": "bookmark-wrapper", children: bookmarks &&
                    bookmarks.map(bookmark => (_jsx(Bookmark, { bookmark: bookmark.bookmark, id: bookmark.id, callStartTime: callStartTime?.clientCreatedAt, updateBookmark: updateBookmark, deleteBookmark: () => {
                            deleteBookmark(bookmark.id);
                        } }, bookmark.id))) })] }));
};
const Bookmark = ({ bookmark, callStartTime, deleteBookmark, id, updateBookmark, }) => {
    const theme = useTheme();
    const [timestampToDisplay, setTimestampToDisplay] = useState('');
    useEffect(() => {
        if (callStartTime) {
            const videoStartTime = callStartTime.seconds.low + callStartTime.nanos / 1000000000;
            const bookmarkTime = bookmark.timestamp.seconds.low + bookmark.timestamp.nanos / 1000000000;
            setTimestampToDisplay(convertTimeStampToReadableString(bookmarkTime - videoStartTime));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [callStartTime]);
    const updateBookmarkHandler = (label) => {
        updateBookmark(id, bookmark, label);
    };
    return (_jsxs(BookmarkItemWrapper, { "data-testid": id, children: [_jsx("div", { children: _jsx(AppIcon, { color: theme.colors.black, name: "BookmarkIcon" }) }), _jsx(AppText, { size: "mediumLarge", style: { marginLeft: theme.spacing.m, marginRight: theme.spacing.m }, children: timestampToDisplay }), _jsx(BookmarkInput, { updateBookmark: updateBookmarkHandler, id: id, bookmark: bookmark }), _jsx(IconButton, { label: "Delete Bookmark", "data-testid": "delete-bookmark-button", iconName: "TrashIcon", fill: theme.colors.black, hoverFill: theme.colors.red50, background: "transparent", onClick: deleteBookmark })] }, id));
};
const BookmarkInput = ({ bookmark, id, updateBookmark, }) => {
    const theme = useTheme();
    const [label, setLabel] = useState(bookmark?.label || '');
    const [originalLabel, setOriginalLabel] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const inputEl = useRef(null);
    const [bookmarkToSelect, clearBookmarkToSelect] = useBookmarkToSelect();
    useEffect(() => {
        if (bookmarkToSelect?.id === id) {
            inputEl.current?.focus();
            clearBookmarkToSelect();
        }
    }, [id, bookmarkToSelect, clearBookmarkToSelect, inputEl]);
    function checkUpdateBookmark() {
        if (label != originalLabel) {
            updateBookmark(label);
        }
    }
    return (_jsx(BookmarkInputWrapper, { children: _jsx(BaseInput, { ref: inputEl, fontSize: 2, "data-testid": `bookmark-label-${id}`, backgroundColor: isFocused ? 'white' : 'transparent', border: isFocused ? '2px solid' : '0px solid', borderColor: theme.colors.azure, borderRadius: '6px', py: 3, px: 3, hideLabel: true, value: label, name: bookmark?.label || '', placeholder: "Untitled Bookmark", onFocus: () => {
                setIsFocused(true);
                setOriginalLabel(label);
                inputEl.current?.select();
            }, onBlur: () => {
                setIsFocused(false);
                checkUpdateBookmark();
            }, onKeyPress: e => {
                if (e.key === 'Enter') {
                    inputEl.current?.blur();
                }
            }, onKeyDown: e => {
                if (e.key === 'Escape') {
                    setLabel(originalLabel);
                    // Wait a tick to blur so that label gets updated
                    setTimeout(() => {
                        inputEl.current?.blur();
                    }, 10);
                }
            }, onChange: e => {
                setLabel(e.target.value);
            } }) }));
};
const BookmarkListWrapper = styled.div({
    marginTop: '16px',
});
const BookmarkItemWrapper = styled.li(({ theme }) => ({
    display: 'flex',
    marginBottom: '12px',
    alignItems: 'center',
    backgroundColor: theme.colors.grey05,
    borderRadius: 4,
    padding: theme.spacing.m,
    height: 60,
}));
const BookmarkInputWrapper = styled.div(({ theme }) => ({
    ':hover': {
        backgroundColor: theme.colors.pumpedHoverBlue,
    },
    width: '100%',
    borderRadius: '6px',
}));
const convertTimeStampToReadableString = (secondsElapsed) => {
    const minutes = Math.floor(secondsElapsed / 60);
    const seconds = Math.floor(secondsElapsed % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
};
//# sourceMappingURL=in_call_bookmarks.js.map